import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { EVENT } from "@/lib/constants";
import { Check, Calendar, Clock, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import EventDateBadge from "@/components/EventDateBadge";

export default function ThankYou() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div className="absolute top-6 right-6 sm:right-12 z-20">
        <LanguageToggle />
      </div>
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/12418667/pexels-photo-12418667.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 sm:px-12 pt-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl w-full text-center"
        >
          <div style={{ transform: "translateY(-24px)" }} data-testid="app-received-check-wrap">
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.3, type: "spring" }}
              data-testid="app-received-check"
              className="w-20 h-20 mx-auto mb-2 border-2 border-[#F5C518] flex items-center justify-center"
            >
              <Check className="w-10 h-10 text-[#F5C518]" strokeWidth={2} />
            </motion.div>
          </div>

          <div
            data-testid="app-received-kicker"
            className={isRTL ? "label-kicker mb-10" : "label-kicker mb-6"}
            style={{ fontSize: isRTL ? "40px" : "32px", transform: "translateY(-16px)" }}
          >
            {t("appReceived")}
          </div>

          <h1
            data-testid="thanks-title"
            className={`font-heading font-extrabold text-white leading-[0.95] mb-6 ${
              isRTL ? "text-5xl sm:text-6xl lg:text-7xl" : "text-4xl sm:text-5xl lg:text-6xl"
            }`}
          >
            {t("thanksL1")} <span className="text-[#F5C518]">{t("thanksL2")}</span>.
          </h1>

          <p className="font-body text-white/60 text-base sm:text-lg max-w-lg mx-auto mb-6 leading-relaxed">
            {t("thanksSub")}
          </p>

          <div className="flex justify-center mb-8">
            <EventDateBadge />
          </div>

          <div className="border border-white/10 bg-white/[0.02] p-10 sm:p-12 mb-12 sm:w-[calc(100%+128px)] sm:-mx-16">
            <div className="label-kicker mb-8" style={{ fontSize: "24px" }}>{t("eventDetails")}</div>
            <div className={`grid ${isRTL ? "sm:grid-cols-3" : "sm:grid-cols-[2fr_1fr_1.5fr]"} gap-6 items-start ${isRTL ? "text-center" : "text-left"}`}>
              <div className={`flex items-start gap-3 ${isRTL ? "justify-center" : ""}`}>
                <Calendar
                  className="w-5 h-5 text-[#F5C518] flex-shrink-0 mt-1"
                  style={isRTL ? { marginInlineEnd: "-4px" } : undefined}
                />
                <div>
                  <div className="text-xl uppercase tracking-wider text-white/40">{t("eventDatesKicker")}</div>
                  <div className="font-body text-lg sm:text-xl text-white">{t("evtDate")}</div>
                </div>
              </div>
              <div className={`flex items-start gap-3 ${isRTL ? "justify-center" : ""}`}>
                <Clock className="w-5 h-5 text-[#F5C518] flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xl uppercase tracking-wider text-white/40">{t("evTime")}</div>
                  <div className="font-body text-lg sm:text-xl text-white">{t("evtTime")}</div>
                </div>
              </div>
              <a
                href={EVENT.mapUrl}
                target="_blank"
                rel="noreferrer"
                className={`flex items-start gap-3 hover:text-[#F5C518] transition-colors ${isRTL ? "justify-center" : ""}`}
              >
                <MapPin className="w-5 h-5 text-[#F5C518] flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xl uppercase tracking-wider text-white/40">{t("evLocation")}</div>
                  <div className="font-body text-lg sm:text-xl text-white">{t("evtLocation")}</div>
                </div>
              </a>
            </div>
          </div>

          <button onClick={() => navigate("/")} data-testid="thanks-home-btn" className="btn-ghost">
            {t("returnHome")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
